import React from 'react';
import styled, { keyframes } from 'styled-components';
import { useObserverReturningStore } from '../../../CodesThatsGottaGoSomewhereElse';
import { Line } from '../../../platform/geom/Line';
import { toDegrees } from '../../../platform/geom/Math';
import { Point } from '../../../platform/geom/Point';
import { Rect } from '../../../platform/geom/Rect';
import { useResolve } from '../../Container';
import { Camera } from '../../canvas/Camera';
import { CanvasPing, UIPing } from './Ping';

const dropTransform = keyframes`
    0% { opacity: 0; transform: translateY(-75px); }
    100% { opacity: 1; transform: translateY(0px); }
`;

const upDownTransform = keyframes`
    0% { transform: translateY(0px) }
    50% { transform: translateY(-25px) }
    100% { transform: translateY(0px) }
`;

const fadeOutOpacity = keyframes`
    0% { opacity: 1 }
    90% { opacity: 1 }
    100% { opacity: 0 }
`;

const ArrowSvg = styled.svg<{ ttl: number }>`
    position: absolute;
    top: -4rem;
    left: -1.5rem;
    width: 3rem;
    height: 4rem;
    animation: 
        300ms ${dropTransform} cubic-bezier(0,0,.3,1.01) 1,
        600ms ${upDownTransform} ease-in-out 300ms infinite,
        ${p => p.ttl - 1000}ms ${fadeOutOpacity} linear 1000ms forwards;
`;

const Arrow: React.FC<{ ttl: number } & React.SVGProps<SVGSVGElement>> = ({ ttl, ...props }) =>
    <ArrowSvg ttl={ttl} viewBox="0 0 48 64" fill="none" {...props as any}>
        <path d="M4 39L24 60M24 60L44 39M24 60V4" stroke="currentColor" strokeWidth="8" strokeLinecap="round" strokeLinejoin="round" />
    </ArrowSvg>;

const CanvasArrowMount = styled.div`      
    position: absolute;
    cursor: pointer;
    pointer-events: all;

    &:hover {
        filter: brightness(70%);
    }
`;

const CanvasBoxMount = styled.div<{ ttl: number }>`
    position: absolute;
    border: solid 4px;
    border-radius: 3px;
    
    animation: 
        /* 600ms ${upDownTransform} ease-in-out 300ms infinite, */
        ${p => p.ttl - 1000}ms ${fadeOutOpacity} linear 1000ms forwards;
`;

const UIArrowMount = styled.div`
    position: fixed;
`;

const UIBoxMount = styled.div`
    position: fixed;
    border: solid 4px;
    border-radius: 3px;

    animation: 
        5000ms ${fadeOutOpacity} linear 1000ms forwards;
`;

const computeCanvasArrow = (ping: CanvasPing, camera: Camera): any => {
    const vpt = camera.toView(new Point(ping.region.center().x, ping.region.top));
    const view = camera.getViewFrame().inflate([-100, -40]);
    if (view.contains(vpt)) {
        return {
            color: ping.color,
            transform: `translate(${vpt.x}px, ${vpt.y}px) scale(1.5)`,
        };
    }
    // If not in view, calculate the edge point (where it should appear at the edge) by intersecting the view frame with a line from the
    // view center to the desired view point.
    const ln = new Line(view.center(), vpt);
    const ept = view.lines().map(x => x.intersectPoint(ln)).find(x => !!x) ?? view.project(vpt);
    const ang = Point.vector(vpt, ept).angle(Point.up);
    return {
        color: ping.color,
        transform: `translate(${ept.x}px, ${ept.y}px) scale(1.5) rotate(${toDegrees(ang)}deg)`,
    };
};

const computeCanvasBox = (ping: CanvasPing, camera: Camera): any => {

    const viewRegion = Rect.fromPoints(camera.toView(ping.region));
    const xy = viewRegion.topLeft();
    const wh = viewRegion.size();

    const view = camera.getViewFrame();
    if ((viewRegion.width >= 5 || viewRegion.height >= 5) && view.intersects(viewRegion)) {
        return {
            transform: `translate(${xy.x}px, ${xy.y}px) `,
            width: wh.w,
            height: wh.h,
            borderColor: ping.color,
        };
    }
    return null;
};

export const RenderCanvasPing = React.memo<{ ping: CanvasPing }>(({ ping }) => {

    const camera = useObserverReturningStore(useResolve(Camera));

    const arrowStyle = computeCanvasArrow(ping, camera);
    const boxStyle = computeCanvasBox(ping, camera);

    return <>
        {boxStyle && <CanvasBoxMount ttl={ping.ttl} style={boxStyle} />}
        {arrowStyle && <CanvasArrowMount style={arrowStyle} >
            <Arrow ttl={ping.ttl} onClick={() => camera.animateToFrame(ping.region)} />
        </CanvasArrowMount>}
    </>;

});

const computeUIArrow = (ping: UIPing, camera: Camera): any => {
    const vpt = new Point(ping.region.left + (ping.region.width / 2), ping.region.top + (ping.region.height / 2));
    const view = camera.getViewFrame().inflate([-100, -40]);
    if (view.contains(vpt)) {
        return {
            left: vpt.x,
            top: vpt.y,
            transform: 'scale(1.5)',
            color: ping.color,
        };
    }

    const ln = new Line(view.center(), vpt);
    const intersect = view.lines().find(x => !!x.intersectPoint(ln));
    const index = view.lines().findIndex(x =>
        x.p1.x === intersect.p1.x &&
        x.p1.y === intersect.p1.y &&
        x.p2.x === intersect.p2.x &&
        x.p2.y === intersect.p2.y
    );
    const ang = 90 * ((index + 2) % 4);

    return {
        left: vpt.x,
        top: vpt.y,
        transform: `scale(1.5) rotate(${ang}deg)`,
        color: ping.color,
    };
};

const computeUIBox = (ping: UIPing): any => {
    const xy = ping.region.topLeft();
    const wh = ping.region.size();

    return {
        left: xy.x,
        top: xy.y,
        width: wh.w,
        height: wh.h,
        borderColor: ping.color,
    };
};

export const RenderUIPing = React.memo<{ ping: UIPing }>(({ ping }) => {

    const camera = useObserverReturningStore(useResolve(Camera));

    const arrowStyle = computeUIArrow(ping, camera);
    const boxStyle = computeUIBox(ping);

    return <>
        <UIBoxMount style={boxStyle} />
        <UIArrowMount style={arrowStyle} >
            <Arrow ttl={6000} />
        </UIArrowMount>
    </>;
});

